import { useEffect, useMemo, useRef, useState } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import moment from 'moment';
import * as _ from 'lodash';

import useGetUsedArea2, { UsedArea2 } from 'app/hooks/useGetUsedArea2';
import { getPriceApi } from 'app/services/cartApi';

type UsedArea2Data = {
  showId: number;
  zone_restaurant: number;
  type: 'zone' | 'restaurant' | 'none';
  data: UsedArea2[];
};

type Params = {
  showId: number;
  date: Date;
  zones: number[] | null;
  restaurants: number[] | null;
  isCableTicket: boolean;
  bpAllocatedDebt: string | number | null;
  isLongTerm: boolean | null;
  paymentMethod: string | number;
  amount: number;
  minAmount: number;
  isApiOrder: boolean;
  checkMinUnitPrice: (price: number) => boolean;
};

const useInitData = ({
  showId,
  date,
  zones,
  restaurants,
  isCableTicket,
  bpAllocatedDebt,
  isLongTerm,
  paymentMethod,
  amount,
  minAmount,
  isApiOrder,
  checkMinUnitPrice,
}: Params) => {
  const [priceData, setPriceData] = useState<Record<string, any>[]>([]);
  const [usedArea2Data, setUsedArea2Data] = useState<UsedArea2Data[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const { onFetchData } = useGetUsedArea2({
    payloadData: {},
    applyUsedZone2: !isCableTicket,
    applyZone: !isCableTicket,
    isRestaurant: !!restaurants && restaurants?.length > 0,
    IIFE: false,
  });

  const zonesMemoized = useDeepCompareMemo(() => zones, [zones]);
  const restaurantsMemoized = useDeepCompareMemo(
    () => restaurants,
    [restaurants],
  );
  const onFetchUsedArea2 = useRef(onFetchData);
  const checkValidMinUnitPrice = useRef(checkMinUnitPrice);
  const paymentMethodInit = useRef(paymentMethod);

  useEffect(() => {
    const checkValidPriceData = (priceData: Record<string, any>) => {
      const inventory = +(priceData?.inventory ?? 0);
      const error = priceData?.error;
      const errorTime = priceData?.errorTime;
      const errorTicket = priceData?.errorTicket;
      const price = priceData?.price;
      const originalPrice = priceData?.originalPrice;
      if (
        !!error ||
        !!errorTime ||
        !!errorTicket ||
        inventory < amount ||
        inventory < minAmount ||
        minAmount > amount ||
        _.isNil(originalPrice) ||
        _.isNil(price) ||
        !checkValidMinUnitPrice.current(price)
      )
        return false;
      return true;
    };

    const fetchShowData = async () => {
      setLoading(true);
      if (isCableTicket) {
        const formData = new FormData();
        formData.append(`forceTicketType`, !!isLongTerm ? '1' : '0');
        if (isApiOrder) formData.append('numberChannel', '50');
        formData.append('notValidateAmount', '1');
        formData.append(`items[0][date]`, moment(date).format('YYYY-MM-DD'));
        formData.append(`items[0][show_id]`, showId.toString());
        formData.append(`items[0][amount]`, amount.toString());
        formData.append(
          `items[0][payment]`,
          paymentMethodInit.current.toString(),
        );
        if (bpAllocatedDebt !== null && bpAllocatedDebt !== undefined)
          formData.append(
            `items[0][bpAllocatedDebt]`,
            bpAllocatedDebt.toString(),
          );

        try {
          const res = await getPriceApi(formData);
          setPriceData([
            {
              ...res.data?.[0],
              _fe_check_valid: checkValidPriceData(res.data),
            },
          ]);
        } catch (error: any) {
        } finally {
          setLoading(false);
        }

        return;
      }

      const createUsedArea2Promises = () => {
        const usedArea2Promises: Promise<UsedArea2Data>[] = [];

        const generateUsedArea2Promises = (
          usedArea1: number[] | ['undefined'],
          type: 'zone' | 'restaurant' | 'none',
        ) => {
          usedArea1?.forEach(id => {
            usedArea2Promises.push(
              onFetchUsedArea2
                .current({
                  payloadData: {
                    date: moment(date).format('YYYY-MM-DD'),
                    show_id: showId,
                    ...(type === 'none'
                      ? {}
                      : type === 'restaurant'
                      ? { restaurant_id: id }
                      : { zone_id: id }),
                    forceTicketType: !!isLongTerm ? '1' : '0',
                    ...(bpAllocatedDebt ? { bpAllocatedDebt } : {}),
                  },
                })
                .then(response => {
                  return {
                    data: response?.data ?? [],
                    zone_restaurant: id,
                    type,
                    showId,
                  };
                }),
            );
          });
        };

        generateUsedArea2Promises(zonesMemoized ?? [], 'zone');
        generateUsedArea2Promises(restaurantsMemoized ?? [], 'restaurant');
        if (!zonesMemoized?.length && !restaurantsMemoized?.length)
          generateUsedArea2Promises(['undefined'], 'none');

        return usedArea2Promises;
      };

      const usedArea2Promises = createUsedArea2Promises();

      try {
        const usedArea2Response = await Promise.all(usedArea2Promises);
        setUsedArea2Data(usedArea2Response);

        const createGetPricePromises = () => {
          const getPricePromises: Promise<Record<string, any>>[] = [];

          usedArea2Response.forEach(
            ({ data: usedArea2Data, zone_restaurant, type }) => {
              if (!usedArea2Data.length) return;
              const formData = new FormData();
              formData.append(`forceTicketType`, !!isLongTerm ? '1' : '0');
              formData.append('notValidateAmount', '1');
              if (isApiOrder) formData.append('numberChannel', '50');
              // Giai đoạn init cần gửi tất cả khung giờ để biết option nào còn vé/hợp lệ.
              // Kết quả batch này chỉ nên dùng để dựng danh sách option và chọn default.
              //Do có chính sách khuyến mãi như mua trên 4 vé được giảm giá, mà API này gửi tất cả múi giờ nên sẽ bị 
              // sai chính sách khuyến mãi theo số lượng. Nên sau đó cần get_price lại đúng option đã chọn để lấy giá chính xác.
              usedArea2Data.forEach((i, idx) => {
                formData.append(
                  `items[${idx}][date]`,
                  moment(date).format('YYYY-MM-DD'),
                );
                formData.append(`items[${idx}][show_id]`, showId.toString());
                formData.append(`items[${idx}][amount]`, amount.toString());
                formData.append(
                  `items[${idx}][payment]`,
                  paymentMethodInit.current.toString(),
                );
                if (bpAllocatedDebt !== null && bpAllocatedDebt !== undefined)
                  formData.append(
                    `items[${idx}][bpAllocatedDebt]`,
                    bpAllocatedDebt.toString(),
                  );
                if (type === 'restaurant')
                  formData.append(
                    `items[${idx}][restaurant_id]`,
                    zone_restaurant.toString(),
                  );
                else
                  formData.append(
                    `items[${idx}][zone_id]`,
                    zone_restaurant.toString(),
                  );

                formData.append(`items[${idx}][used_area2]`, i.value);
              });
              getPricePromises.push(
                getPriceApi(formData).then(response => {
                  return response.data?.map(i => ({
                    ...i,
                    _fe_check_valid: checkValidPriceData(i),
                  }));
                }),
              );
            },
          );

          return getPricePromises;
        };

        const getPricePromises = createGetPricePromises();
        const getPriceResponse = await Promise.all(getPricePromises).finally(
          () => {
            setLoading(false);
          },
        );
        setPriceData(getPriceResponse.flatMap(i => i));
      } catch {
      } finally {
        setLoading(false);
      }
    };

    fetchShowData().finally(() => setFetched(true));
  }, [
    bpAllocatedDebt,
    date,
    isCableTicket,
    isLongTerm,
    restaurantsMemoized,
    showId,
    zonesMemoized,
    amount,
    minAmount,
    isApiOrder,
  ]);

  const result = useMemo(
    () => ({
      priceData,
      usedArea2Data,
      loading,
      fetched,
    }),
    [priceData, usedArea2Data, loading, fetched],
  );

  return result;
};

export default useInitData;
